import PropTypes from 'prop-types'
import { memo, useMemo, useRef } from 'react'
import { normalizeSizeString } from 'app/util/sizes'
import animationStyles from 'components/util/animation/animation.module.scss'
import useDelayedActiveState from 'lib/react/hooks/useDelayedActiveState'
import classNames from 'lib/util/classNames'
import styles from './Tooltip.module.scss'
import Body from './components/Body/Body'
import Header from './components/Header/Header'
import Portal from './components/Portal/Portal'
import Target from './components/Target/Target'
import Wrapper from './components/Wrapper/Wrapper'
import usePositionTooltip from './hooks/usePositionTooltip'
import { useTooltipContext } from './provider/TooltipProvider'

/**
 * The `Tooltip`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function TooltipComponent (props) {
  const {
    children,
    className,
    color,
    delayShow,
    distance,
    preferredLocation,
    size
  } = props
  const { tooltipId, tooltipTarget } = useTooltipContext()
  const isActive = useDelayedActiveState(true, delayShow, 0)
  const ref = {
    node: useRef(),
    inner: useRef(),
    outer: useRef()
  }

  const { ordinalDirection, pinPosition } = usePositionTooltip(
    ref,
    preferredLocation,
    distance
  )
  const mode = useMemo(
    () => (/^(e|w)$/.test(ordinalDirection) ? 'v' : 'h'),
    [ordinalDirection]
  )
  const attributes = useMemo(
    () => ({
      className: classNames([
        styles.tooltip,
        className,
        styles[`color-${color}`],
        styles[`mode-${mode}`],
        styles[`direction-${ordinalDirection}`],
        styles[`size-${normalizeSizeString(size)}`],
        isActive && styles.active,
        tooltipTarget && styles.interactive,
        isActive && animationStyles.fadeUp
      ]),
      style: {
        '--pin-x': pinPosition.x + 'px',
        '--pin-y': pinPosition.y + 'px'
      }
    }),
    [
      isActive,
      pinPosition,
      ordinalDirection,
      mode,
      className,
      size,
      color,
      tooltipTarget
    ]
  )

  return (
    <div role="tooltip" id={tooltipId} ref={ref.node} {...attributes}>
      <div ref={ref.inner} className={styles.tooltipInner}>
        <div className={styles.tooltipDisplay}>
          <div className={styles.tooltipBg} />
          {children}
        </div>
      </div>
      <div ref={ref.outer} className={styles.tooltipOuter} aria-hidden="true">
        <div className={styles.tooltipPin} />
      </div>
    </div>
  )
}

/** @type {object} */
TooltipComponent.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  color: PropTypes.string,
  delayShow: PropTypes.number,
  distance: PropTypes.number,
  preferredLocation: PropTypes.number,
  size: PropTypes.string
}

/** @type {object} */
TooltipComponent.defaultProps = {
  children: null,
  className: '',
  color: 'b',
  delayShow: 0.3,
  distance: 30,
  preferredLocation: 180,
  size: 'standard'
}

// Memoize
const Tooltip = memo(TooltipComponent)

// Bind the types
Tooltip.Target = Target
Tooltip.Portal = Portal
Tooltip.Wrapper = Wrapper
Tooltip.Header = Header
Tooltip.Body = Body

// Export
export default Tooltip
